import { Flex, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, useToast } from '@morressier/alembic';
import { useFormContext } from 'react-hook-form';
import { useUpdateJournalSettings } from 'src/api/mutations';
import StepContentFormField from './StepContentFormField';
import {
  DESCRIPTION_MAX_LENGTH,
  HEADER_MAX_LENGTH,
  ISubmissionStepContentForm,
  SubmissionStepKey,
  buildSubmissionStepPayload,
  getStepFieldPath
} from './stepConfig';

const EditStepHeaderConfigModal = ({
  isOpen,
  onClose,
  journalId,
  title,
  stepKey
}: {
  isOpen: boolean;
  onClose: () => void;
  journalId: string;
  title: string;
  stepKey: SubmissionStepKey;
}) => {
  const toast = useToast();

  const {
    handleSubmit,
    getValues,
    reset,
    setValue
  } = useFormContext<ISubmissionStepContentForm>();

  const updateSettingMutation = useUpdateJournalSettings(journalId);

  const headerField = getStepFieldPath(stepKey, 'header');
  const subHeaderField = getStepFieldPath(stepKey, 'subHeader');

  const onSubmit = async () => {
    const header = String(getValues(headerField) ?? '').trim();
    const subHeader = String(getValues(subHeaderField) ?? '').trim();

    setValue(headerField, header, { shouldValidate: true, shouldDirty: true });
    setValue(subHeaderField, subHeader, {
      shouldValidate: true,
      shouldDirty: true
    });

    const res = await updateSettingMutation.mutateAsync(
      buildSubmissionStepPayload(stepKey, {
        header,
        subHeader
      })
    );

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `${title} Edited`,
        isClosable: true
      });
      onClose();
      return;
    }

    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string'
          ? res?.message
          : 'Something went wrong',
      isClosable: true
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isLoading = updateSettingMutation?.isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width={{ base: 'calc(100vw - 16px)', md: '42rem' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction={'column'} gap={4} padding={8}>
          <Flex fontSize={32}>
            <FontAwesomeIcon icon={faEdit} />
          </Flex>
          <Text textStyle="headingGroup">Edit {title}</Text>
          <Flex direction={'column'} gap={4}>
            <StepContentFormField
              label="Header"
              name={headerField}
              placeholder="Enter header"
              maxLength={HEADER_MAX_LENGTH}
            />
            <StepContentFormField
              label="Description"
              name={subHeaderField}
              placeholder="Enter sub header description"
              maxLength={DESCRIPTION_MAX_LENGTH}
              isTextArea
            />
          </Flex>
          <Flex
            justifyContent="flex-end"
            gap={2}
            alignItems="center"
            marginTop={8}
          >
            <Button
              variant="secondary"
              onClick={handleClose}
              isDisabled={isLoading}
            >
              Exit Without Saving
            </Button>
            <Button variant="primary" type="submit" isLoading={isLoading}>
              Save Changes
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default EditStepHeaderConfigModal;
